// React
import React, { useState, useMemo } from "react";

// Libraries
import PropTypes from "prop-types";
import { get, isEmpty, map, filter, orderBy, uniqBy, includes } from "lodash";

// Redux
import { connect } from "react-redux";
import { sendMessage } from "@Actions/chats";

// Components
import NewTable from "@Components/NewTable";
import IKModal from "@Components/IkModal";

// Utils
import downloadFile from "@Actions/downloads";
import { messageBuilder } from "@Utils/functionality";

// Styles
import styles from "./styles.module.css";

const Share = ({ shareMessage, onClose, dispatch, chats, userName }) => {
	const [selectedContacts, setSelectedContacts] = useState([]);

	// Obtener contactos únicos de los chats activos para mostrarlos en la tabla
	const contactsData = useMemo(() => {
		if (isEmpty(chats)) return [];

		const contactsList = map(chats, (chat) => {
			return {
				id: get(chat, "contact.id"),
				contactId: get(chat, "contactId"),
				name: get(chat, "contact.name", ""),
				phone: get(chat, "contact.phone", ""),
				email: get(chat, "contact.email", ""),
				platform: get(chat, "channel.platform.name", ""),
				channelLabel: get(chat, "channel.label", ""),
				chatId: get(chat, "id"),
				channelCid: get(chat, "channelCid"),
				channel: get(chat, "channel"),
			};
		});

		const contactsWithId = filter(contactsList, (contact) => {
			return contact.contactId;
		});

		const uniqueContacts = uniqBy(contactsWithId, "contactId");

		return orderBy(uniqueContacts, ["name"], ["asc"]);
	}, [chats]);

	// Maneja la selección múltiple de contactos en la tabla
	const handleMultipleContacts = (selectedItems) => {
		setSelectedContacts(selectedItems || []);
	};

	// Prepara el archivo adjunto para reenvío, descargándolo si es necesario
	const prepareFile = async (messageUri, messageType) => {
		const attachmentTypes = ["Image", "Audio", "Video", "Document"];
		if (!messageUri || !includes(attachmentTypes, messageType)) {
			return { file: null, finalUri: messageUri };
		}

		const isLocalUri = messageUri.indexOf("localhost") !== -1 || messageUri.indexOf("blob") !== -1 || messageUri.indexOf("file") !== -1;

		let fileUri = messageUri;

		if (!isLocalUri) {
			try {
				fileUri = await dispatch(downloadFile({ uri: messageUri }));
			} catch {
				return { file: null, finalUri: messageUri };
			}
		}

		try {
			const response = await fetch(fileUri);
			const blob = await response.blob();
			const fileName = messageUri.split("/").pop() || `file_${Date.now()}`;
			const file = new File([blob], fileName, { type: blob.type });
			return { file, finalUri: fileUri };
		} catch {
			return { file: null, finalUri: messageUri };
		}
	};

	// Reenvía el mensaje a los contactos seleccionados
	const onShare = async () => {
		if (isEmpty(selectedContacts) || isEmpty(shareMessage)) {
			onClose();
			return;
		}

		const messageType = get(shareMessage, "type", "Text");
		const messageText = get(shareMessage, "text", "");
		const messageUri = get(shareMessage, "uri");

		const { file, finalUri } = await prepareFile(messageUri, messageType);

		const validChats = map(selectedContacts, (contact) => {
			const chatId = get(contact, "chatId");
			if (!chatId) return null;
			return chats.find((chat) => {
				return get(chat, "id") === chatId;
			});
		}).filter(Boolean);

		for (const targetChat of validChats) {
			const newMessage = messageBuilder({
				chat: targetChat,
				type: messageType,
				reply: {},
				userName,
				text: messageText,
				uri: finalUri,
			});

			await dispatch(sendMessage({ message: newMessage, file, newChat: false }));

			if (validChats.length > 1) {
				await new Promise((resolve) => {
					return setTimeout(resolve, 100);
				});
			}
		}

		onClose();
	};

	const selectedCount = selectedContacts.length;
	const saveLabel = selectedCount > 0 ? `Reenviar (${selectedCount})` : "Reenviar";

	return (
		<IKModal
			title="Reenviar Mensaje"
			onClose={onClose}
			onSuccess={onShare}
			fullHeight
			saveLabel={saveLabel}
			saveDisabled={selectedCount === 0}>
			<div className={styles.grid}>
				<div className={styles.tableContainer}>
					<NewTable
						data={contactsData}
						columns={[
							{ label: "", key: "chatId", multiple: true, width: 1 },
							{ label: "ID", key: "id", format: "string", width: 1 },
							{ label: "Plataforma", key: "platform", format: "string", width: 1 },
							{ label: "Nombre", key: "name", format: "string", width: 1 },
							{ label: "Teléfono", key: "phone", format: "phone", width: 1 },
							{ label: "Email", key: "email", format: "string", headerPosition: "left", width: 15 },
						]}
						onMultipleChange={handleMultipleContacts}
						multipleSelected={selectedContacts}
						keyField="chatId"
						searchable
						searchDropdownPosition="right"
						pageRows={15}
						defaultOrder={{ key: "name", dir: "asc" }}
						showRowSelector
						mobileCardView
						cardLine1={[
							{ key: "id", label: "ID" },
							{ key: "name", label: "" },
						]}
						cardLine2={[
							{ key: "phone", label: "Tel: " },
							{ key: "email", label: "Email: " },
						]}
					/>
				</div>
			</div>
		</IKModal>
	);
};

Share.propTypes = {
	shareMessage: PropTypes.object,
	onClose: PropTypes.func,
	dispatch: PropTypes.func,
	chats: PropTypes.array,
	userName: PropTypes.string,
};

function mapStateToProps({ chats, user }) {
	return {
		chats: chats.chats,
		userName: user.name,
	};
}

export default connect(mapStateToProps)(Share);
